// /admin/schedules — pick an agent, edit their week.
//
// The schedule grid is one of the three screens design §7 says cannot be generic,
// so it does not go through the descriptor framework. The unit here is a WEEK: the
// endpoint replaces every row in one transaction, because a save that replaced one
// row at a time would pass through the 0-row state, and 0 rows is the state the
// router reads as "never on shift" — a crash mid-save would take the agent out of
// rotation rather than leaving them the week it was replacing.
//
// No `mx-auto max-w-…` wrapper. AppLayout's <main> already is that container
// (../../layout/AppLayout.jsx), and repeating it here would nest one max-width
// inside another and double the page padding. Every other page relies on the same
// thing — the note at the top of Console.jsx says so.
//
// PRESENTATION, NEVER AUTHORIZATION. Nothing here decides access:
// `requireProductRole('support', 'lead')` on the router plus the per-route
// `assertRank` are what refuse, and this page happily mounts for an agent who types
// the URL and reports what the API said.

import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { apiFetch, ApiError } from '../../api/client.js';
import ScheduleGrid from '../../components/admin/ScheduleGrid.jsx';
import {
  scheduleEffect, weeklyHours, formatHours, timeZoneLabel, DEFAULT_TIMEZONE,
} from '../../components/admin/schedule-coverage.js';
import { consoleErrorMessage } from './descriptor-form.js';
import { matchAgents, scheduleSummary } from './agent-search.js';
import { supportPath } from '../../support-path.js';

/**
 * A refusal, in the most specific words available.
 *
 * A 422 from this endpoint carries the sentence that matters in
 * `details.fields[].message` — including the acknowledgement rail, whose whole
 * value is the paragraph explaining what emptying a week costs. `consoleErrorMessage` is the
 * fallback and is the one place FORBIDDEN and PRODUCT_NOT_LICENSED are told apart.
 */
function refusalText(error) {
  if (error instanceof ApiError && Array.isArray(error.details?.fields)
      && error.details.fields.length > 0) {
    return error.details.fields.map((field) => String(field?.message ?? '').trim())
      .filter(Boolean).join(' ');
  }
  return consoleErrorMessage(error);
}

/**
 * Who has a week on file, and what it is.
 *
 * THE LIST THAT DID NOT EXIST. Before the routing rule inverted, an unscheduled agent
 * was on shift 24/7, so "who is configured?" was idle curiosity. Now an agent with no
 * schedule is never on shift, so the people on this list ARE the people who can receive
 * a ticket — and a queue whose members are all missing from it assigns nothing at all.
 *
 * Editing an existing week starts here rather than by remembering a name and typing it.
 */
function ScheduledRoster({ scheduled, selectedId, onPick }) {
  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold text-ink">
        Scheduled <span className="font-normal text-ink-muted">({scheduled.length})</span>
      </h2>

      {scheduled.length === 0 ? (
        // NOT an empty box. With this rule, nobody scheduled means the desk assigns
        // nothing, and that is the single most important sentence this screen can say.
        <p className="mt-2 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">
          Nobody has a schedule yet. An agent with no schedule is never on shift, so
          round-robin will not assign a ticket to anyone. Search for an agent above and
          draw their week.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-line rounded-lg border border-line bg-surface">
          {scheduled.map((agent) => (
            <li key={agent.user_id}>
              <button
                type="button"
                onClick={() => onPick(agent.user_id)}
                className={`flex w-full flex-wrap items-baseline justify-between gap-x-4 gap-y-1
                            px-3 py-2 text-left hover:bg-sunken focus-visible:outline-2
                            focus-visible:outline-offset-2 focus-visible:outline-brand-600
                            dark:focus-visible:outline-brand-400
                            ${String(agent.user_id) === selectedId ? 'bg-sunken' : ''}`}
              >
                <span className="text-sm font-medium text-ink">{agent.display_name}</span>
                <span className="text-xs text-ink-secondary">{scheduleSummary(agent.rows)}</span>
                {/* Two states that make a drawn week meaningless, said here rather than
                    discovered when a ticket does not arrive. Both are gates the router
                    applies BEFORE the shift check (src/routing/eligibility.js), so a
                    perfect schedule under either one assigns nothing. */}
                {agent.is_active === false && (
                  <span className="rounded bg-ink/10 px-1.5 py-0.5 text-xs text-ink-secondary">
                    account inactive
                  </span>
                )}
                {agent.is_active !== false && agent.can_be_assigned === false && (
                  <span className="rounded bg-ink/10 px-1.5 py-0.5 text-xs text-ink-secondary">
                    cannot own tickets
                  </span>
                )}
                <span className="w-full text-xs text-ink-muted">{agent.timezone}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function Schedules() {
  const [agents, setAgents] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [term, setTerm] = useState('');
  const [zones, setZones] = useState([]);
  const [userId, setUserId] = useState('');
  const [saved, setSaved] = useState({ timezone: null, rows: [] });
  const [timezone, setTimezone] = useState('');
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  // False from the moment the agent changes until that agent's week has actually
  // arrived. Gates Save so a week can never be written against an agent whose
  // own week was never loaded. See the effect below.
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Derived, not stored: keeping a filtered copy in state means two sources of truth
  // for one list, and the stale one always wins an argument eventually.
  const matches = matchAgents(agents, term);
  const scheduledById = new Map(scheduled.map((a) => [String(a.user_id), a]));

  useEffect(() => {
    const controller = new AbortController();
    // THREE INDEPENDENT LOOKUPS, SETTLED INDEPENDENTLY.
    //
    // This was one `Promise.all` over two calls, and the failure mode is worse than it
    // looks: a single rejection skips ALL the setState calls, so one hiccup leaves the
    // agent list empty, the zones empty and the roster reading "Nobody has a schedule
    // yet" — which is not "we could not load this", it is a confident and false
    // statement that the desk will assign nothing. Seen live, with five agents
    // scheduled: a dev-server restart 502'd one call and the screen said nobody was.
    //
    // The create screen already reasons this way — "a lookup that fails leaves its
    // picker empty and the ticket still creatable, rather than taking the screen down
    // over a dropdown" (pages/tickets/New.jsx) — and the roster has the stronger claim
    // on it, because its empty state is a sentence about routing rather than a blank
    // list.
    const failures = [];
    const load = (path, apply) => apiFetch(path, { signal: controller.signal })
      .then((body) => apply(body.data))
      .catch((err) => { if (err?.name !== 'AbortError') failures.push(refusalText(err)); });

    Promise.all([
      load('/users?can_be_assigned=true', setAgents),
      load('/admin/time-zones', setZones),
      load('/admin/agent-schedules', setScheduled),
    ]).then(() => {
      // Named as a LOAD failure, so an empty roster below reads as "not loaded"
      // rather than as "nobody is scheduled".
      if (failures.length) setError(`Some of this screen did not load. ${failures[0]}`);
    });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    // Cleared on EVERY change of agent, including to "—". A confirmation left
    // standing from the previous agent would name their hours and, if accepted,
    // send their acknowledgement against somebody else's week.
    setPending(null);
    setNotice(null);
    // `rows` and `saved` are cleared here for the same reason `pending` is, and
    // the omission was a real hole: they used to hold the PREVIOUS agent's week
    // until this GET resolved, and permanently if it rejected — the `.catch`
    // only sets an error, leaving the grid mounted and Save enabled. Pressing it
    // then PUT the previous agent's whole week onto this one.
    //
    // The rail does not catch that. It fires on a change in ROW COUNT — N→0 asks
    // for `acknowledge_no_schedule` — and one populated week replacing another is
    // N→N, so the save is accepted silently, in one transaction, with no
    // confirmation shown.
    //
    // `loaded` gates the editor rather than just blanking the rows: an empty
    // `rows` renders as "never on shift", which is the truth for an agent with no
    // schedule and a lie about one whose week has simply not arrived yet — and
    // here that lie is the one that gets a real week cleared by accident.
    setLoaded(false);
    setSaved({ timezone: null, rows: [] });
    setRows([]);
    if (!userId) return undefined;
    const controller = new AbortController();
    apiFetch(`/admin/agent-schedules/${userId}`, { signal: controller.signal })
      .then((body) => {
        setSaved(body.data);
        setRows(body.data.rows);
        setTimezone(body.data.timezone ?? DEFAULT_TIMEZONE);
        setError(null);
        setLoaded(true);
      })
      .catch((err) => { if (err?.name !== 'AbortError') setError(refusalText(err)); });
    return () => controller.abort();
  }, [userId]);

  async function put(extra) {
    setBusy(true);
    try {
      const body = await apiFetch(`/admin/agent-schedules/${userId}`, {
        method: 'PUT',
        body: { timezone, rows, ...extra },
      });
      setSaved(body.data);
      setRows(body.data.rows);
      setTimezone(body.data.timezone ?? timezone);
      setPending(null);
      setError(null);
      // The roster is now stale in the one way that matters: this save either put
      // somebody INTO rotation or took them out of it, and the list below claims to
      // name exactly who can receive a ticket. Re-read rather than patched in place —
      // the server is the only thing that knows what it actually stored, and a locally
      // spliced row would drift from it the first time a save is partially refused.
      //
      // Fail-soft: the week saved, and a roster that failed to refresh is a stale list,
      // not a lost edit. Reporting an error here would blame the save for it.
      apiFetch('/admin/agent-schedules')
        .then((roster) => setScheduled(roster.data))
        .catch(() => {});
      // The saved state in the one sentence that matters, read back from what the
      // server actually stored rather than from what was typed.
      setNotice(body.data.rows.length === 0
        ? 'Saved. This agent now has NO schedule, which means they are never on shift. '
          + 'Routing will skip them entirely and they will not be assigned any ticket.'
        : `Saved. This agent is now on shift ${formatHours(weeklyHours(body.data.rows))} `
          + 'a week, the coverage drawn below.');
    } catch (err) {
      setPending(null);
      setError(refusalText(err));
    } finally {
      setBusy(false);
    }
  }

  function save() {
    // The confirmation carries the actual hours being given up, because "Are you
    // sure?" is a button people press. The server refuses the same transition
    // regardless (422), so this is the affordance, not the rail.
    setNotice(null);
    const effect = scheduleEffect(saved.rows, rows);
    if (effect.needsConfirmation) return setPending(effect);
    return void put({});
  }

  return (
    <div>
      <Link to={supportPath('/admin')} className="text-sm text-ink-secondary hover:underline">← Admin</Link>
      <h1 className="mt-2 text-xl font-semibold text-ink">Agent schedules</h1>

      {/* schedule_exceptions was deliberately cut (013:5, "the cheapest thing to
          cut"). Said here rather than discovered in December. */}
      <p className="mt-2 text-sm text-ink-secondary">
        Holidays are not modelled. There is no exception or time-off table anywhere in this
        system, so an agent on leave whose weekly shift covers the moment a ticket arrives
        will be assigned it. The only way to stop that is to remove them from the queue&rsquo;s
        members.
      </p>

      {error && (
        <div className="mt-4 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">{error}</div>
      )}

      {notice && !error && (
        <div className="mt-4 rounded-lg border border-line bg-sunken p-4 text-sm text-ink">{notice}</div>
      )}

      <label htmlFor="schedule-agent" className="mt-6 block text-sm font-medium text-ink">
        Find an agent
      </label>
      <input
        id="schedule-agent"
        type="search"
        className="field-input"
        placeholder="Type a name or email"
        autoComplete="off"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />

      {/* The matches, as a list rather than a dropdown. A `<select>` made this screen a
          LOOKUP: it required already knowing whose name to scroll to, and it could not
          show anything about a person beside their name — least of all whether they
          have a schedule, which is now the only thing that decides whether routing can
          pick them at all. */}
      {term.trim() && (
        <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-line bg-surface">
          {matches.length === 0 && (
            <li className="px-3 py-2 text-sm text-ink-muted">Nobody matches “{term.trim()}”.</li>
          )}
          {matches.map((agent) => {
            const summary = scheduleSummary(scheduledById.get(String(agent.id))?.rows);
            return (
              <li key={agent.id}>
                <button
                  type="button"
                  onClick={() => { setUserId(String(agent.id)); setTerm(''); }}
                  className={`flex w-full items-baseline justify-between gap-4 px-3 py-2 text-left
                              hover:bg-sunken focus-visible:outline-2 focus-visible:outline-offset-2
                              focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400
                              ${String(agent.id) === userId ? 'bg-sunken' : ''}`}
                >
                  <span className="text-sm font-medium text-ink">{agent.display_name}</span>
                  {/* The state that matters, on the row where the choice is made. */}
                  <span className={`shrink-0 text-xs ${summary ? 'text-ink-secondary' : 'text-ink-muted'}`}>
                    {summary ?? 'No schedule'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ScheduledRoster
        scheduled={scheduled}
        selectedId={userId}
        onPick={(id) => { setUserId(String(id)); setTerm(''); }}
      />

      {userId && (
        <>
          <label htmlFor="schedule-tz" className="mt-4 block text-sm font-medium text-ink">
            Time zone
          </label>
          <select
            id="schedule-tz"
            className="field-input"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
          >
            {/* Windows ids from sys.time_zone_info, in the order the endpoint sends
                them — sorted on the season-stable (standard, daylight) pair, which
                is why this must not re-sort. `Eastern Standard Time (Mexico)`
                (-05:00, no DST), `SA Eastern Standard Time` (-03:00) and `AUS
                Eastern Standard Time` (+10:00) are all real, all accepted, and all
                one alphabetical scroll from the right answer; `timeZoneLabel` is
                what keeps them apart in January as well as in August. */}
            {zones.map((zone) => (
              <option key={zone.name} value={zone.name}>{timeZoneLabel(zone)}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-muted">
            Every shift below is read in this zone. One zone applies to the whole week.
          </p>

          <ScheduleGrid rows={rows} onChange={setRows} />

          <div className="mt-6 flex items-center gap-3">
            <button type="button" className="btn-primary" onClick={save} disabled={busy || !loaded}>
              {busy ? 'Saving…' : 'Save the week'}
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={busy}
              onClick={() => { setRows(saved.rows); setPending(null); setNotice(null); setError(null); }}
            >
              Discard changes
            </button>
          </div>
        </>
      )}

      {pending && (
        <div role="alertdialog" aria-label="Confirm schedule change"
             className="mt-4 rounded-lg border border-line-strong bg-sunken p-4">
          <p className="text-sm text-ink">{pending.message}</p>
          <div className="mt-3 flex gap-3">
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => { void put({ [pending.acknowledgeField]: true }); }}
            >
              {/* `cleared` is the only kind that asks now — `scheduleEffect` returns
                  needsConfirmation: false for every other transition — so the label is
                  unconditional rather than a ternary whose other arm cannot be reached
                  and would read "Narrow them to 0 hours" if it ever were. */}
              Take them out of rotation
            </button>
            <button type="button" className="btn-ghost ring-1 ring-line-strong"
                    disabled={busy}
                    onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
